// Camera enumeration + ranking. The single most important piece of the iPhone fix:
// enumerate back-facing cameras and prefer the standard back lens over the
// ultrawide (which has a fixed minimum focus distance ~10cm and blurs close
// stickers) and the telephoto (too-narrow field of view).

export function rankCamera(label: string): number {
  const l = label.toLowerCase();
  let score = 0;
  if (/ultra\s?wide|ultrawide/.test(l)) score -= 10;
  if (/telephoto/.test(l)) score -= 5;
  if (/^back camera$|^rear camera$|main/.test(l)) score += 10;
  if (/dual|triple/.test(l)) score += 5;
  if (/back|rear|environment/.test(l)) score += 1;
  return score;
}

export async function enumerateBackCameras(): Promise<MediaDeviceInfo[]> {
  if (!navigator.mediaDevices?.enumerateDevices) return [];

  let devices = await navigator.mediaDevices.enumerateDevices();
  let cams = devices.filter((d) => d.kind === 'videoinput');

  // iOS Safari and most browsers return empty labels until camera permission
  // has been granted at least once. If labels are empty, prime permission with
  // a brief environment-facing stream, then re-enumerate.
  const allLabelsEmpty = cams.length > 0 && cams.every((c) => !c.label);
  if (allLabelsEmpty) {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' } },
      });
      stream.getTracks().forEach((t) => t.stop());
      devices = await navigator.mediaDevices.enumerateDevices();
      cams = devices.filter((d) => d.kind === 'videoinput');
    } catch {
      return cams;
    }
  }

  const back = cams.filter((c) => /back|rear|environment/i.test(c.label));
  return back.length ? back : cams;
}

export async function pickBestCameraId(): Promise<string | null> {
  const cams = await enumerateBackCameras();
  if (!cams.length) return null;
  const ranked = cams
    .map((c) => ({ c, score: rankCamera(c.label) }))
    .sort((a, b) => b.score - a.score);
  return ranked[0].c.deviceId || null;
}

export async function setTorch(track: MediaStreamTrack, on: boolean): Promise<boolean> {
  try {
    await track.applyConstraints({
      advanced: [{ torch: on } as MediaTrackConstraintSet],
    });
    return true;
  } catch {
    return false;
  }
}

export function trackSupportsTorch(track: MediaStreamTrack | undefined): boolean {
  if (!track) return false;
  const caps = track.getCapabilities?.() as (MediaTrackCapabilities & { torch?: boolean }) | undefined;
  return Boolean(caps?.torch);
}
